import { useEffect, useState } from 'react';
import { parseCoverDoc, type CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { useGetCoverMakerLeadQuery } from '../../../redux/api/fymAPI';
import { browserCoverStorage, readCarry, readPublicCover } from '../publicMode/publicCoverStore';
import { loadPublicUpload } from '../publicMode/publicUploadStore';
import { coverCarrySource } from './carriedDoc';

export interface CarriedDesign {
  doc: CoverDoc;
  /** The kept bytes of an own picture of the visitor; null with none. */
  upload: Blob | null;
}

/**
 * The design that a visitor of the public tool carries into the account (design g4): the saved
 * design of the lead (`m24CoverMakerCarry` holds its token), else the design of this browser.
 * `undefined` while it loads; null when there is nothing to carry.
 */
export function useCarriedDesign(): CarriedDesign | null | undefined {
  const [source] = useState(() => coverCarrySource(readCarry(browserCoverStorage())));
  const token = source.kind === 'lead' ? source.token : '';
  const lead = useGetCoverMakerLeadQuery({ token }, { skip: source.kind !== 'lead' });
  const [result, setResult] = useState<CarriedDesign | null | undefined>(undefined);

  useEffect(() => {
    if (result !== undefined) {
      return undefined;
    }
    if (source.kind === 'lead' && lead.data === undefined && lead.error === undefined) {
      return undefined;
    }
    let isCancelled = false;
    const resolve = async (): Promise<CarriedDesign | null> => {
      const leadDoc = lead.data === undefined ? null : parseCoverDoc(lead.data.doc);
      // A lead link that does not work falls back to the design of this browser.
      const local = source.kind === 'none' && leadDoc === null ? null : readPublicCover(browserCoverStorage());
      const doc = leadDoc ?? local?.doc ?? null;
      if (doc === null) {
        return null;
      }
      return { doc, upload: leadDoc === null ? await loadPublicUpload() : null };
    };
    resolve()
      .then((value) => {
        if (!isCancelled) {
          setResult(value);
        }
      })
      .catch(console.error);
    return () => {
      isCancelled = true;
    };
  }, [lead.data, lead.error, result, source.kind]);

  return result;
}
